import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { KeyRound, ArrowLeft } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [tokenHash, setTokenHash] = useState<string>('');
  const [resetType, setResetType] = useState<string>('');

  useEffect(() => {
    const checkTokens = async () => {
      try {
        // Get parameters from URL hash fragments (Supabase PKCE flow)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type');
        const tokenHashFromUrl = hashParams.get('token_hash');
        
        // Also check URL search params as fallback
        const urlType = searchParams.get('type');
        const urlTokenHash = searchParams.get('token_hash');
        
        // Check for access_token and refresh_token in hash (alternative flow)
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        
        const finalType = type || urlType;
        const finalTokenHash = tokenHashFromUrl || urlTokenHash;

        const debug = {
          hashFragment: window.location.hash,
          searchParams: window.location.search,
          type: finalType,
          hasTokenHash: !!finalTokenHash,
          tokenHash: finalTokenHash,
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          fullUrl: window.location.href
        };
        
        setDebugInfo(debug);
        console.log('Reset password debug info:', debug);

        // Handle different token formats
        if (accessToken && refreshToken) {
          // Handle OAuth-style tokens
          console.log('Using OAuth-style tokens for password reset');
          setTokenHash(accessToken); // Use access_token as the verification token
          setResetType('recovery');
          setValidatingToken(false);
          return;
        }

        if (!finalTokenHash || finalType !== 'recovery') {
          console.error('Missing or invalid reset parameters:', debug);
          toast.error('Invalid reset link. Please request a new password reset.');
          navigate('/forgot-password');
          return;
        }

        // Store the token hash and type for later use
        setTokenHash(finalTokenHash);
        setResetType(finalType);

        console.log('Reset link validation complete - tokens found');
        setValidatingToken(false);
      } catch (error) {
        console.error('Error processing reset link:', error);
        setDebugInfo(prev => ({ ...prev, error: error.message }));
        toast.error('Error processing reset link. Please try again.');
        navigate('/forgot-password');
      }
    };

    checkTokens();
  }, [navigate, searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (!tokenHash) {
      toast.error('Reset token is missing. Please request a new password reset.');
      navigate('/forgot-password');
      return;
    }

    try {
      setLoading(true);
      
      console.log('Attempting password reset with token verification...');

      // Check if we have OAuth-style tokens (access_token + refresh_token)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (accessToken && refreshToken) {
        // Handle OAuth-style password reset flow
        console.log('Using OAuth-style password reset flow');
        
        // Set the session using the tokens
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });

        if (sessionError) {
          console.error('Session setup error:', sessionError);
          throw new Error('Invalid reset link. Please request a new password reset.');
        }

        if (!sessionData.session) {
          throw new Error('Failed to establish session. Please request a new password reset.');
        }

        console.log('Session established successfully, updating password...');
        
        // Update password with the established session
        const { error: updateError } = await supabase.auth.updateUser({
          password: password
        });

        if (updateError) {
          console.error('Password update error:', updateError);
          throw updateError;
        }

        console.log('Password updated successfully');
        toast.success('Password has been reset successfully! You can now log in with your new password.');
        
        // Sign out to ensure clean state
        await supabase.auth.signOut();
        
        // Redirect to login
        navigate('/login');
        return;
      }

      // Fallback to OTP verification flow
      console.log('Using OTP verification flow');
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'recovery'
      });

      console.log('Token verification result:', { 
        success: !!data, 
        error: error?.message,
        user: data?.user?.id,
        session: !!data?.session
      });

      if (error) {
        console.error('Token verification error:', error);
        
        if (error.message.includes('expired') || error.message.includes('invalid')) {
          throw new Error('Reset link has expired or is invalid. Please request a new password reset.');
        }
        
        throw error;
      }

      // Now that we have a valid session, update the password
      console.log('Token verified successfully, updating password...');
      
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        console.error('Password update error:', updateError);
        throw updateError;
      }

      console.log('Password updated successfully');
      toast.success('Password has been reset successfully! You can now log in with your new password.');
      
      // Sign out to ensure clean state
      await supabase.auth.signOut();
      
      // Redirect to login
      navigate('/login');
    } catch (error) {
      console.error('Error resetting password:', error);
      if (error instanceof Error) {
        if (error.message.includes('expired') || error.message.includes('invalid')) {
          toast.error(error.message);
          navigate('/forgot-password');
        } else {
          toast.error(`Error resetting password: ${error.message}`);
        }
      } else {
        toast.error('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-white mb-4">Validating reset link...</div>
          {Object.keys(debugInfo).length > 0 && (
            <div className="text-xs text-gray-400 max-w-md">
              <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-zinc-900 rounded-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <KeyRound className="h-6 w-6 text-primary-500" />
            <h1 className="text-2xl font-bold text-white">Create New Password</h1>
          </div>

          {/* Debug info in development */}
          {import.meta.env.DEV && Object.keys(debugInfo).length > 0 && (
            <div className="mb-4 p-3 bg-zinc-800 rounded text-xs text-gray-400">
              <details>
                <summary className="cursor-pointer">Debug Info (Dev Only)</summary>
                <pre className="mt-2 whitespace-pre-wrap">{JSON.stringify(debugInfo, null, 2)}</pre>
              </details>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              label="New Password"
              required
              minLength={6}
            />

            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              label="Confirm New Password"
              required
              minLength={6}
              showStrengthMeter={false}
            />

            <div className="pt-4 flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <button
                type="submit"
                disabled={loading || password !== confirmPassword || password.length < 6}
                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>

          <div className="mt-6 p-4 bg-green-900 bg-opacity-50 rounded-lg">
            <h3 className="text-green-300 font-semibold mb-2">Password Requirements</h3>
            <ul className="text-green-200 text-sm space-y-1">
              <li>• At least 6 characters long</li>
              <li>• Include a mix of letters, numbers, and symbols for better security</li>
              <li>• Both password fields must match</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}